import React from 'react';
import {useCurrencyFormat} from '~/entities/currencies';
import {RsnButton, RsnDialog} from '~/library/ui-kit';
import {OperableAccount} from '~/redux/finances/domain';
import styles from './CardLimitModal.module.scss';

export interface CardLimitsModalProps {
    readonly account: OperableAccount;
    readonly type: 'single' | 'daily' | 'monthly' | null;
    readonly maximalPerOperationTotal?: string;
    readonly maximalPerDayTotal?: string;
    readonly maximalPerDayCurrent?: string;
    readonly maximalPerMonthTotal?: string;
    readonly maximalPerMonthCurrent?: string;
}

function CardLimitsModal({
    account,
    type,
    maximalPerOperationTotal,
    maximalPerDayTotal,
    maximalPerDayCurrent,
    maximalPerMonthTotal,
    maximalPerMonthCurrent,
}: CardLimitsModalProps) {
    const {onClose} = RsnDialog.useControls();

    const {format} = useCurrencyFormat(account, {
        parts: ['symbol', 'amount'],
    });

    function renderRestrictions() {
        switch (type) {
            case 'single':
                return (
                    <div className={styles.text}>
                        You can top up your account with no more than{' '}
                        <span className={styles.bold}>{format(maximalPerOperationTotal ?? 0)}</span> at a time.
                    </div>
                );
            case 'daily':
                return (
                    <div className={styles.text}>
                        You can top up your account with no more than{' '}
                        <span className={styles.bold}>{format(maximalPerDayTotal ?? 0)}</span> per day.{' '}
                    </div>
                );
            case 'monthly':
                return (
                    <div className={styles.text}>
                        You can top up your account with no more than{' '}
                        <span className={styles.bold}>{format(maximalPerMonthTotal ?? 0)}</span> per month.{' '}
                    </div>
                );
        }
    }

    function renderAvailable() {
        switch (type) {
            case 'daily':
                return (
                    <div className={`${styles.text} ${styles.available}`}>
                        Available for today: <span className={styles.bold}>{format(maximalPerDayCurrent ?? 0)}.</span>
                    </div>
                );
            case 'monthly':
                return (
                    <div className={`${styles.text} ${styles.available}`}>
                        Available for this month:{' '}
                        <span className={styles.bold}>{format(maximalPerMonthCurrent ?? 0)}.</span>
                    </div>
                );
            default:
                return null;
        }
    }

    return (
        <RsnDialog.Window>
            <RsnDialog.CenteredHeader
                text={`You have exceeded ${type} payment limit`}
                icon={{element: <RsnDialog.StatusIcon status={'attention'} />}}
            />
            <RsnDialog.Body>
                {renderRestrictions()}
                {renderAvailable()}
            </RsnDialog.Body>
            <RsnDialog.Footer>
                <RsnButton.Secondary type='button' onClick={onClose}>
                    I got it
                </RsnButton.Secondary>
            </RsnDialog.Footer>
        </RsnDialog.Window>
    );
}

export default CardLimitsModal;
